import React, { useState, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { format, subMonths } from 'date-fns';
import { DollarSign, AlertCircle, RefreshCw } from 'lucide-react';
import useMarketData from '../hooks/useMarketData.js';

const ASSETS = [
  { key: 'btc', label: 'Bitcoin', color: '#f7931a' },
  { key: 'gold', label: 'Gold', color: '#f0c040' },
  { key: 'sp500', label: 'S&P 500', color: '#22c55e' },
  { key: 'nasdaq', label: 'Nasdaq', color: '#06b6d4' },
  { key: 'm2', label: 'M2 Supply', color: '#a78bfa' },
];

const TIME_OPTIONS = [
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
  { label: '2Y', months: 24 },
  { label: '5Y', months: 60 },
  { label: 'Max', months: 300 },
];

function interpretCorrelation(r, label) {
  if (r == null) return 'Insufficient data';
  const abs = Math.abs(r);
  if (abs < 0.1) return `${label} moves independently of M2 changes`;
  if (r > 0.7) return `${label} tracks M2 expansion closely`;
  if (r > 0.4) return `${label} has moderate sensitivity to M2 growth`;
  if (r > 0.1) return `${label} shows weak positive linkage to M2`;
  if (r > -0.1) return `${label} moves independently of M2 changes`;
  if (r > -0.4) return `${label} shows weak inverse relationship with M2`;
  if (r > -0.7) return `${label} moves counter to M2 — other drivers dominate`;
  return `${label} strongly diverges from M2 direction`;
}

export default function M2CorrelationOverlay() {
  const market = useMarketData();
  const { btc, gold, fred, equityHistory } = market;

  const [lookbackMonths, setLookbackMonths] = useState(24);
  const [hiddenAssets, setHiddenAssets] = useState(new Set());

  const m2Series = fred.m2 || [];
  const btcSeries = btc.history || [];
  const goldSeries = gold.history || [];
  const sp500Series = equityHistory.sp500 || [];
  const nasdaqSeries = equityHistory.nasdaq || [];

  const seriesMap = { m2: m2Series, btc: btcSeries, gold: goldSeries, sp500: sp500Series, nasdaq: nasdaqSeries };

  const coreLoading = fred.status === 'loading';
  const hasM2 = m2Series.length > 0;
  const hasAnyAsset = btcSeries.length > 0 || goldSeries.length > 0 || sp500Series.length > 0 || nasdaqSeries.length > 0;
  const noData = !hasM2 || !hasAnyAsset;

  const toggleAsset = useCallback((key) => {
    setHiddenAssets(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const { chartData, latestGrowth, correlations } = useMemo(() => {
    const cutoff = subMonths(new Date(), lookbackMonths);
    const m2Map = mapByMonth(m2Series);
    const btcMap = mapByMonth(btcSeries);
    const goldMap = mapByMonth(goldSeries);
    const sp500Map = mapByMonth(sp500Series);
    const nasdaqMap = mapByMonth(nasdaqSeries);

    const months = allMonthsSince([m2Series, btcSeries, goldSeries, sp500Series, nasdaqSeries], cutoff);
    if (!months.length) return { chartData: [], latestGrowth: {}, correlations: [] };

    const firstKey = monthKey(months[0]);
    const bases = {
      m2: m2Map.get(firstKey) || 1,
      btc: btcMap.get(firstKey) || null,
      gold: goldMap.get(firstKey) || null,
      sp500: sp500Map.get(firstKey) || null,
      nasdaq: nasdaqMap.get(firstKey) || null,
    };
    const maps = { m2: m2Map, btc: btcMap, gold: goldMap, sp500: sp500Map, nasdaq: nasdaqMap };

    const data = months.map(d => {
      const k = monthKey(d);
      const row = { date: format(d, 'MMM yy'), ts: d.getTime() };
      for (const { key } of ASSETS) {
        const val = maps[key].get(k);
        row[key] = val != null && bases[key] ? ((val / bases[key] - 1) * 100) : null;
      }
      return row;
    });

    const last = data[data.length - 1] || {};
    const growth = {};
    for (const { key } of ASSETS) {
      growth[key] = last[key] != null ? last[key] : null;
    }

    const m2Filtered = filterSince(m2Series, cutoff);
    const corrs = ASSETS.filter(a => a.key !== 'm2').map(({ key, label, color }) => ({
      key, label, color,
      r: computeCorrelation(m2Filtered, filterSince(seriesMap[key], cutoff)),
    }));

    return { chartData: data, latestGrowth: growth, correlations: corrs };
  }, [m2Series, btcSeries, goldSeries, sp500Series, nasdaqSeries, lookbackMonths]);

  if (coreLoading && noData) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-24 rounded-xl bg-slate-900 border border-slate-800" />
        <div className="h-80 rounded-2xl bg-slate-900 border border-slate-800" />
        <div className="h-20 rounded-xl bg-slate-900 border border-slate-800" />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-300">M2 data unavailable</h3>
            <p className="text-sm text-slate-400 mt-1">Could not load M2 money supply or asset history data.</p>
            <button onClick={() => { market.refetch.fred(); market.refetch.equityHistory(); }}
                    className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 transition-colors">
              <RefreshCw className="w-3 h-3" /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-violet-500/[0.03] via-slate-900/60 to-cyan-500/[0.03] backdrop-blur-xl p-4 md:p-6">
      <div className="absolute -top-24 -left-24 w-72 h-72 bg-violet-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="relative space-y-5">

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-violet-400" />
              M2 Money Supply Correlation
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Normalized growth rebased to start of window — click cards to show/hide
            </p>
          </div>
          <div className="inline-flex bg-slate-900/60 border border-white/10 rounded-lg p-0.5">
            {TIME_OPTIONS.map(opt => (
              <button key={opt.label} onClick={() => setLookbackMonths(opt.months)}
                      className={`px-2.5 py-1 text-xs font-mono rounded-md transition-colors ${
                        lookbackMonths === opt.months
                          ? 'bg-violet-500 text-white font-bold'
                          : 'text-slate-300 hover:bg-white/5'
                      }`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards — above the chart */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {ASSETS.map(({ key, label, color }) => {
            const growth = latestGrowth[key];
            const isHidden = hiddenAssets.has(key);
            return (
              <button
                key={key}
                onClick={() => toggleAsset(key)}
                className={`text-left px-3 py-2.5 rounded-lg border transition-all duration-200 ${
                  isHidden
                    ? 'bg-slate-900/20 border-slate-800 opacity-50'
                    : 'bg-slate-900/40 border-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className={`w-2 h-2 rounded-full transition-opacity ${isHidden ? 'opacity-30' : ''}`}
                        style={{ background: color }} />
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{label}</span>
                </div>
                <div className="font-mono font-bold tabular-nums text-base" style={{ color: isHidden ? '#475569' : color }}>
                  {growth != null ? `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%` : '—'}
                </div>
              </button>
            );
          })}
        </div>

        {/* Chart */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`}
              />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
              {ASSETS.map(({ key, color }) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={color}
                  strokeWidth={key === 'm2' ? 3 : 2}
                  strokeOpacity={hiddenAssets.has(key) ? 0 : 1}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              ))}
              <Tooltip
                cursor={{ stroke: 'rgba(255,255,255,0.18)', strokeWidth: 1, strokeDasharray: '3 3' }}
                content={<M2Tooltip hiddenAssets={hiddenAssets} />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Correlation cards with interpretation */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {correlations.map(({ key, label, color, r }) => {
            const isHidden = hiddenAssets.has(key);
            return (
              <button
                key={key}
                onClick={() => toggleAsset(key)}
                className={`text-left px-4 py-3 rounded-xl border transition-all duration-200 ${
                  isHidden
                    ? 'bg-slate-900/20 border-slate-800 opacity-50'
                    : 'bg-slate-900/40 border-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                    <span className="text-xs font-medium text-slate-300">{label}</span>
                  </div>
                  <span className={`font-mono font-bold text-sm tabular-nums ${
                    r == null ? 'text-slate-600'
                      : r > 0.7 ? 'text-emerald-400'
                      : r > 0.4 ? 'text-blue-400'
                      : r > 0 ? 'text-amber-400'
                      : 'text-red-400'
                  }`}>
                    {r != null ? `r = ${r.toFixed(2)}` : '—'}
                  </span>
                </div>
                {r != null && (
                  <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.abs(r) * 100}%`,
                        background: r > 0.7 ? '#34d399' : r > 0.4 ? '#60a5fa' : r > 0 ? '#fbbf24' : '#f87171',
                      }}
                    />
                  </div>
                )}
                <p className="text-[11px] text-slate-500 leading-snug">
                  {interpretCorrelation(r, label)}
                </p>
              </button>
            );
          })}
        </div>

        {/* M2 Liquidity Signals */}
        <LiquiditySignals
          m2Series={m2Series}
          sp500Series={sp500Series}
          nasdaqSeries={nasdaqSeries}
        />

        {/* Insight strip */}
        <InsightStrip
          correlations={correlations}
          latestGrowth={latestGrowth}
          lookbackMonths={lookbackMonths}
        />
      </div>
    </section>
  );
}

function LiquiditySignals({ m2Series, sp500Series, nasdaqSeries }) {
  const signals = useMemo(() => {
    if (!m2Series.length) return null;

    const latestM2 = m2Series[m2Series.length - 1]?.value;
    const latestDate = m2Series[m2Series.length - 1]?.date;

    // M2 YoY — compare latest to same month one year prior
    const m2YoY = (() => {
      if (m2Series.length < 13) return null;
      const targetMonth = latestDate.getMonth();
      const targetYear = latestDate.getFullYear() - 1;
      const prior = m2Series.find(p =>
        p.date.getFullYear() === targetYear && p.date.getMonth() === targetMonth
      );
      if (!prior || !prior.value) return null;
      return ((latestM2 / prior.value - 1) * 100);
    })();

    // M2 3-month momentum
    const m2_3m = (() => {
      if (m2Series.length < 4) return null;
      const prior = m2Series[m2Series.length - 4]?.value;
      if (!prior) return null;
      return ((latestM2 / prior - 1) * 100);
    })();

    // M2 direction — rising or falling (6mo trend)
    const m2Direction = (() => {
      if (m2Series.length < 7) return null;
      const prior = m2Series[m2Series.length - 7]?.value;
      if (!prior) return null;
      return latestM2 > prior ? 'rising' : 'falling';
    })();

    // S&P 500 market cap / M2 ratio (using index as proxy)
    // M2 is in billions, index is points — we normalize to a ratio scale
    // that's comparable over time. Common method: (SPX / M2_trillions)
    const sp500M2Ratio = (() => {
      if (!sp500Series.length || !latestM2) return null;
      const latestSp500 = sp500Series[sp500Series.length - 1]?.value;
      if (!latestSp500) return null;
      return latestSp500 / (latestM2 / 1000); // M2 in trillions
    })();

    // Nasdaq / M2 multiple
    const nasdaqM2Ratio = (() => {
      if (!nasdaqSeries.length || !latestM2) return null;
      const latestNasdaq = nasdaqSeries[nasdaqSeries.length - 1]?.value;
      if (!latestNasdaq) return null;
      return latestNasdaq / (latestM2 / 1000); // M2 in trillions
    })();

    // Dot-com peak reference: Mar 2000 Nasdaq ~5,048, M2 ~$4.66T → ratio ~1,083
    const dotcomNasdaqM2 = 1083;

    return { m2YoY, m2_3m, m2Direction, sp500M2Ratio, nasdaqM2Ratio, dotcomNasdaqM2, latestM2, latestDate };
  }, [m2Series, sp500Series, nasdaqSeries]);

  if (!signals) return null;

  const { m2YoY, m2_3m, m2Direction, sp500M2Ratio, nasdaqM2Ratio, dotcomNasdaqM2 } = signals;

  return (
    <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.03] p-4">
      <div className="text-[10px] uppercase tracking-widest text-violet-400 font-bold mb-3">
        M2 Liquidity Signals
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* M2 YoY */}
        <SignalCard
          label="M2 YoY"
          value={m2YoY != null ? `${m2YoY >= 0 ? '+' : ''}${m2YoY.toFixed(1)}%` : '—'}
          tone={m2YoY == null ? 'neutral' : m2YoY > 5 ? 'bullish' : m2YoY > 0 ? 'neutral' : 'bearish'}
          sub={m2YoY != null ? (m2YoY > 5 ? 'Expansion — bullish for risk assets' : m2YoY > 0 ? 'Slow growth — neutral' : 'Contraction — bearish with 6-12mo lag') : null}
        />
        {/* M2 3-month change */}
        <SignalCard
          label="M2 3mo Change"
          value={m2_3m != null ? `${m2_3m >= 0 ? '+' : ''}${m2_3m.toFixed(2)}%` : '—'}
          tone={m2_3m == null ? 'neutral' : m2_3m > 1 ? 'bullish' : m2_3m > 0 ? 'neutral' : 'bearish'}
          sub={m2Direction === 'rising' ? 'Trend: rising (6mo)' : m2Direction === 'falling' ? 'Trend: falling (6mo)' : null}
        />
        {/* S&P 500 / M2 */}
        <SignalCard
          label="S&P 500 / M2"
          value={sp500M2Ratio != null ? Math.round(sp500M2Ratio).toLocaleString() : '—'}
          tone={sp500M2Ratio == null ? 'neutral' : sp500M2Ratio > 330 ? 'caution' : sp500M2Ratio > 250 ? 'neutral' : 'bullish'}
          sub={sp500M2Ratio != null ? (sp500M2Ratio > 330 ? 'Elevated vs liquidity base' : 'Reasonable vs liquidity base') : null}
        />
        {/* Nasdaq / M2 */}
        <SignalCard
          label="Nasdaq / M2"
          value={nasdaqM2Ratio != null ? Math.round(nasdaqM2Ratio).toLocaleString() : '—'}
          tone={nasdaqM2Ratio == null ? 'neutral' : nasdaqM2Ratio > dotcomNasdaqM2 ? 'bearish' : nasdaqM2Ratio > dotcomNasdaqM2 * 0.8 ? 'caution' : 'neutral'}
          sub={nasdaqM2Ratio != null ? `Dot-com peak: ${dotcomNasdaqM2.toLocaleString()}${nasdaqM2Ratio > dotcomNasdaqM2 ? ' — exceeded' : ''}` : null}
        />
      </div>
    </div>
  );
}

function SignalCard({ label, value, tone, sub }) {
  const toneColors = {
    bullish: { text: 'text-emerald-400', border: 'border-emerald-500/30', dot: 'bg-emerald-400' },
    bearish: { text: 'text-red-400', border: 'border-red-500/30', dot: 'bg-red-400' },
    caution: { text: 'text-amber-400', border: 'border-amber-500/30', dot: 'bg-amber-400' },
    neutral: { text: 'text-slate-300', border: 'border-white/10', dot: 'bg-slate-400' },
  };
  const t = toneColors[tone] || toneColors.neutral;

  return (
    <div className={`px-3 py-2.5 rounded-lg border bg-slate-900/40 ${t.border}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
        <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      </div>
      <div className={`font-mono font-bold tabular-nums text-lg ${t.text}`}>
        {value}
      </div>
      {sub && <p className="text-[10px] text-slate-500 mt-1 leading-snug">{sub}</p>}
    </div>
  );
}

function InsightStrip({ correlations, latestGrowth, lookbackMonths }) {
  const insights = useMemo(() => {
    const out = [];

    const m2Growth = latestGrowth.m2;
    if (m2Growth != null) {
      const regime = m2Growth > 10 ? 'rapid expansion' : m2Growth > 5 ? 'moderate growth' : m2Growth > 0 ? 'slow growth' : 'contraction';
      out.push({
        title: 'Liquidity Regime',
        body: `M2 is in ${regime} mode (${m2Growth >= 0 ? '+' : ''}${m2Growth.toFixed(1)}% over ${lookbackMonths <= 12 ? lookbackMonths + 'mo' : (lookbackMonths / 12).toFixed(0) + 'yr'}).`,
      });
    }

    const bestAsset = ['btc', 'gold', 'sp500', 'nasdaq']
      .map(k => ({ key: k, g: latestGrowth[k] }))
      .filter(x => x.g != null)
      .sort((a, b) => b.g - a.g)[0];
    if (bestAsset) {
      const lbl = ASSETS.find(a => a.key === bestAsset.key)?.label;
      out.push({
        title: 'Best Performer',
        body: `${lbl} leads at +${bestAsset.g.toFixed(0)}% over this window, ${
          (m2Growth != null && bestAsset.g > m2Growth * 3) ? 'far outpacing M2 growth.' : 'pacing ahead of monetary base expansion.'
        }`,
      });
    }

    const weakest = correlations.filter(c => c.r != null).sort((a, b) => a.r - b.r)[0];
    if (weakest && weakest.r < 0.2) {
      out.push({
        title: 'Decoupling Signal',
        body: `${weakest.label} (r=${weakest.r.toFixed(2)}) is driven more by idiosyncratic factors than M2 flow — don't assume "money printer = instant pump."`,
      });
    }

    return out;
  }, [correlations, latestGrowth, lookbackMonths]);

  if (!insights.length) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {insights.map((ins, i) => (
        <div key={i} className="bg-slate-900/60 border border-white/10 rounded-lg px-4 py-3">
          <div className="text-[10px] uppercase tracking-widest text-violet-400 font-bold mb-1">{ins.title}</div>
          <p className="text-xs text-slate-300 leading-relaxed">{ins.body}</p>
        </div>
      ))}
    </div>
  );
}

function M2Tooltip({ active, payload, hiddenAssets }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/95 backdrop-blur-md px-4 py-3 shadow-xl text-xs font-mono tabular-nums">
      <div className="text-slate-200 font-semibold mb-2 font-sans">{row.date}</div>
      <div className="space-y-1">
        {ASSETS.map(({ key, label, color }) => {
          if (hiddenAssets?.has(key)) return null;
          const v = row[key];
          if (v == null) return null;
          return (
            <div key={key} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                <span className="text-slate-400 font-sans">{label}:</span>
              </div>
              <span className="text-slate-100">{v >= 0 ? '+' : ''}{v.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function monthKey(d) { return `${d.getFullYear()}-${d.getMonth()}`; }

function mapByMonth(series) {
  const m = new Map();
  for (const p of series || []) {
    if (p?.date && Number.isFinite(p.value)) m.set(monthKey(p.date), p.value);
  }
  return m;
}

function allMonthsSince(seriesArr, cutoff) {
  const set = new Map();
  for (const s of seriesArr) {
    for (const p of s || []) {
      if (!p?.date) continue;
      if (cutoff && p.date < cutoff) continue;
      const k = monthKey(p.date);
      if (!set.has(k)) set.set(k, new Date(p.date.getFullYear(), p.date.getMonth(), 1));
    }
  }
  return Array.from(set.values()).sort((a, b) => a - b);
}

function filterSince(series, cutoff) {
  return (series || []).filter(p => p?.date && p.date >= cutoff);
}

function computeCorrelation(seriesA, seriesB) {
  if (!seriesA?.length || !seriesB?.length) return null;
  const aMap = mapByMonth(seriesA);
  const bMap = mapByMonth(seriesB);

  const pairs = [];
  for (const [k, aVal] of aMap) {
    const bVal = bMap.get(k);
    if (bVal != null) pairs.push([aVal, bVal]);
  }

  if (pairs.length < 6) return null;

  const aVals = pairs.map(p => p[0]);
  const bVals = pairs.map(p => p[1]);

  return pearson(logReturns(aVals), logReturns(bVals));
}

function logReturns(vals) {
  const ret = [];
  for (let i = 1; i < vals.length; i++) {
    if (vals[i - 1] > 0 && vals[i] > 0) {
      ret.push(Math.log(vals[i] / vals[i - 1]));
    }
  }
  return ret;
}

function pearson(a, b) {
  const n = Math.min(a.length, b.length);
  if (n < 5) return null;
  const mx = a.slice(0, n).reduce((s, v) => s + v, 0) / n;
  const my = b.slice(0, n).reduce((s, v) => s + v, 0) / n;
  let num = 0, dx = 0, dy = 0;
  for (let i = 0; i < n; i++) {
    const ax = a[i] - mx, ay = b[i] - my;
    num += ax * ay;
    dx += ax * ax;
    dy += ay * ay;
  }
  return dx && dy ? num / Math.sqrt(dx * dy) : null;
}
